// Sync-Tab — Anmeldung an der zentralen AMed-DB + Statusanzeige
// (Design: docs/ZENTRALE-DB-PLAN.md)

import React, { useCallback, useEffect, useState } from 'react';
import {
  getServerUrl, setServerUrl, getSyncAccount, getSyncStatus,
  syncLogin, syncLogout, syncNow, syncRegister, queueFullPush,
  uploadSalt, ensureKeyCheck,
  type SyncStatus,
} from '../../services/syncService';
import { logAktion } from '../../services/auditLogService';

export function SyncSektion(): React.JSX.Element {
  const [account,  setAccount]  = useState<string | null>(null);
  const [status,   setStatus]   = useState<SyncStatus | null>(null);
  const [server,   setServer]   = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [msg,      setMsg]      = useState<{ text: string; ok: boolean } | null>(null);

  const refresh = useCallback((): void => {
    void getSyncAccount().then(setAccount);
    void getSyncStatus().then(setStatus);
  }, []);

  useEffect(() => {
    void getServerUrl().then(setServer);
    refresh();
    window.addEventListener('amed:sync-status', refresh);
    return () => { window.removeEventListener('amed:sync-status', refresh); };
  }, [refresh]);

  const doAuth = async (mode: 'login' | 'register'): Promise<void> => {
    setBusy(true);
    setMsg(null);
    try {
      await setServerUrl(server);
      if (mode === 'login') {
        await syncLogin(email, password);
      } else {
        await syncRegister(email, password);
      }
      /* Multi-Device-Grundlage: Salt hinterlegen + Passwort-Kontrollwert. */
      await uploadSalt();
      await ensureKeyCheck();
      /* Erstmigration: beim allerersten Verbinden alles hochladen. */
      const alreadyPushed = status !== null && status.lastSyncAt !== null;
      if (!alreadyPushed) {
        const n = await queueFullPush();
        setMsg({ text: `Verbunden. ${n} Datensätze für Upload vorgemerkt.`, ok: true });
      } else {
        setMsg({ text: 'Verbunden.', ok: true });
      }
      void logAktion('SYSTEM', 'SYNC_VERBUNDEN');
      setPassword('');
      await syncNow();
      refresh();
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async (): Promise<void> => {
    await syncLogout();
    void logAktion('SYSTEM', 'SYNC_GETRENNT');
    refresh();
  };

  /* Abgelaufene Sitzung ohne „Trennen" erneuern: E-Mail bleibt, nur Passwort. */
  const doReLogin = async (): Promise<void> => {
    if (account === null || password === '') return;
    setBusy(true);
    setMsg(null);
    try {
      await syncLogin(account, password);
      setPassword('');
      await syncNow();
      void logAktion('SYSTEM', 'SYNC_VERBUNDEN');
      setMsg({ text: 'Wieder verbunden.', ok: true });
      refresh();
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const fmtTime = (iso: string | null): string =>
    iso === null ? '—' : new Date(iso).toLocaleString('de-AT');

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Synchronisiert Ihre Daten verschlüsselt mit Ihrer eigenen zentralen Datenbank
        (EU-Server, {server.replace(/^https?:\/\//, '') || 'amed.dr-karrer.com'}).
        Die App bleibt offline voll nutzbar; Änderungen werden bei Verbindung abgeglichen.
        Namen und Geburtsdaten bleiben zusätzlich Ende-zu-Ende verschlüsselt.
      </p>

      {account === null ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="label-text">Server-URL</label>
            <input type="url" value={server} onChange={e => { setServer(e.target.value); }}
              placeholder="https://amed.dr-karrer.com" className="input-field" />
          </div>
          <div className="space-y-1">
            <label className="label-text">E-Mail</label>
            <input type="email" value={email} onChange={e => { setEmail(e.target.value.trim()); }}
              placeholder="mail@dr-karrer.com" className="input-field"
              autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </div>
          <div className="space-y-1">
            <label className="label-text">Sync-Passwort</label>
            <input type={showPw ? 'text' : 'password'} value={password}
              onChange={e => { setPassword(e.target.value); }}
              placeholder="mind. 12 Zeichen" className="input-field"
              autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer mt-1">
              <input type="checkbox" checked={showPw}
                onChange={e => { setShowPw(e.target.checked); }}
                className="rounded border-gray-300 text-blue-600" />
              👁 Passwort anzeigen
            </label>
            <p className="text-xs text-gray-400">
              Eigenes Server-Passwort — unabhängig vom lokalen Master-Passwort.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { void doAuth('login'); }} disabled={busy}
              className="btn-primary text-sm disabled:opacity-50">
              {busy ? 'Verbinde…' : 'Anmelden'}
            </button>
            <button onClick={() => { void doAuth('register'); }} disabled={busy}
              className="text-sm text-blue-700 dark:text-blue-400 font-medium hover:underline disabled:opacity-50">
              Erstes Konto anlegen (einmalig)
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {status?.authExpired === true && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                ⚠ Sitzung abgelaufen — bitte neu anmelden
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Ihre Daten und {status.pendingCount} offene Änderung(en) sind sicher gespeichert
                und werden nach der Anmeldung hochgeladen. Kein „Trennen" nötig.
              </p>
              <input type={showPw ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') { void doReLogin(); } }}
                placeholder="Sync-Passwort" className="input-field"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} />
              <div className="flex items-center gap-3">
                <button onClick={() => { void doReLogin(); }} disabled={busy || password === ''}
                  className="btn-primary text-sm disabled:opacity-50">
                  {busy ? 'Melde an…' : 'Neu anmelden'}
                </button>
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={showPw}
                    onChange={e => { setShowPw(e.target.checked); }}
                    className="rounded border-gray-300 text-blue-600" />
                  👁 anzeigen
                </label>
              </div>
            </div>
          )}
          <div className="space-y-2 text-sm">
            {[
              ['Konto',            account],
              ['Server',           server],
              ['Letzter Abgleich', fmtTime(status?.lastSyncAt ?? null)],
              ['Ausstehend',       `${status?.pendingCount ?? 0} Änderung(en)`],
              ['Status',           status?.running === true ? '🔄 synchronisiert…'
                                   : status?.lastError != null ? `⚠ ${status.lastError}`
                                   : '✓ bereit'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <span className="text-gray-500 flex-shrink-0">{k}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 text-right break-all">{v}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { void syncNow().then(refresh); }}
              disabled={status?.running === true}
              className="btn-primary text-sm disabled:opacity-50">
              Jetzt synchronisieren
            </button>
            <button onClick={() => { void doLogout(); }}
              className="text-sm text-red-600 hover:text-red-700 font-medium hover:underline">
              Trennen
            </button>
          </div>
          <p className="text-xs text-gray-400">
            „Trennen" stoppt nur den Abgleich auf diesem Gerät — am Server und lokal wird nichts gelöscht.
          </p>
        </div>
      )}

      {msg !== null && (
        <p className={`text-sm font-medium ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
          {msg.ok ? '✓' : '✕'} {msg.text}
        </p>
      )}
    </div>
  );
}
